"use client";

//region Imports
import React, {useEffect, useMemo, useRef, useState, useCallback} from "react";
import {useForm} from "react-hook-form";
import Moment from "moment";
import _ from "lodash";

import {$core, HttpStatus} from "@isudio/core";
import {$store} from '@isudio/globals';
import {config, pages, policies} from "@isudio/config";
import {getCountries, getCountryByCode} from "@isudio/utils";
import type {IUserProfile, IUser, IUserProfilePayload} from "@isudio/data";
import {AccountTypes, GenderCaption} from "@isudio/data";
import type {FooterElement, HeaderElement, BaseDialogProps} from "../";
import {LoadingSpinner, MediaPicker, RowGrid, Confirm, ToolbarTop, ToolbarBottom, InputValidator, PhoneInput, useDialogController, useTippyRegistry} from "../";
//endregion imports

//region Types & Interfaces
interface UserProfileProps extends BaseDialogProps {
  dialogOptions?: any;
  user: IUser;
  onSuccess?: () => void;
  onClose?: () => void;
}
//endregion Types & Interfaces

export function UserProfile({
                              dialogOptions,
                              user,
                              onSuccess,
                              onClose
                            }: UserProfileProps) {
  //region Variables
  const [loading, setLoading] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isOverflow, setIsOverflow] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const showFooter = !isOverflow || isAtBottom;
  const {name} = dialogOptions;
  const className = name.toLowerCase().replace(/ /g, "-");

  const _user = _.cloneDeep(user);
  const isAdmin = _user.accountType === AccountTypes.admin && !_.isNothing(_user.email);
  const isBizAdmin = _user.accountType === AccountTypes.bizAdmin && !_.isNothing(_user.email);
  const isStaff = _user.accountType === AccountTypes.staff && !_.isNothing(_user.email);
  const isStudent = _user.accountType === AccountTypes.student && !_.isNothing(_user.email);

  const defaultProfilePic = pages.userProfile.mediaPicker.defaultProfilePic;
  const [isUnsaved, setIsUnsaved] = useState<boolean>(false);
  const initialPhoto = _user?.photo?.thumbnail || defaultProfilePic;
  const originalPhoto = _user?.photo?.url || defaultProfilePic;
  const [photo, setPhoto] = useState<string | null>(initialPhoto);
  const [image, setImage] = useState<any | null>(null);
  const [defaultImage, setDefaultImage] = useState<string>(defaultProfilePic);
  const [resetKey, setResetKey] = useState(0);

  const mobileRef = useRef<HTMLInputElement | null>(null);
  const lastFocusedRef = useRef<any | null>(null);

  const userPolicy = _.get($store, `Data.userPolicy$.value.fields`, policies.userPolicy.fields);
  const transformCountry = (c: any) => {
    return {
      label: c.name.english,
      value: c.code.phone,
      code: c.code.short2.toLowerCase(),
      imageUrl: `/assets/fonts/flag-icons/flags/png/${c.code.short2.toLowerCase()}.png`
    };
  };

  const country = transformCountry(!_.isNothing(_.get(_user, 'country.code', null)) ? getCountryByCode(_user.country.code) : _.get($store.Device.data, "location.geoCode.country", $store.Data.countries$.getValue().default));
  const countries = getCountries().map((c: any) => {
    return {
      label: c.name,
      value: c.dialCode,
      code: c.code,
      imageUrl: c.imageUrl
    };
  });

  _.set(_user, 'dateOfBirth', Moment(new Date(_user.dateOfBirth)).format('D-MMM-yyyy'));
  _.set(_user, 'gender', GenderCaption[_user.gender]);

  const defaults: IUserProfile = useMemo(() => ({
    accountType: _user.accountType,
    firstName: _user.firstName,
    lastName: _user.lastName,
    socialID: _user.socialID,
    gender: _user.gender,
    dateOfBirth: _user.dateOfBirth,
    email: _user.email,
    country: _user?.country || undefined,
    mobile: _user?.mobile || "",
    organization: (isAdmin || isBizAdmin || isStaff) ? config.app.organization : (_user?.organization || ""),
    designation: _user?.designation || "",
    institution: _user?.institution || "",
    discipline: _user?.discipline || "",
  }), [_user]);

  const {
    register,
    trigger,
    handleSubmit,
    setValue,
    getValues,
    reset,
    setFocus,
    setError,
    formState: {
      isDirty,
      isValid,
      errors
    }
  } = useForm<IUserProfile>({
    criteriaMode: "all",
    defaultValues: defaults
  });
  //endregion Variables

  //region Form Validations
  const validations: any = {
    firstName: {
      required: "First name is required.",
      minLength: {
        value: userPolicy.name.minLength,
        message: `First name should have at least ${userPolicy.name.minLength} characters.`,
      },
      maxLength: {
        value: userPolicy.name.maxLength,
        message: `First name can't exceed ${userPolicy.name.maxLength} characters.`,
      },
      pattern: {
        value: /^[a-zA-Z ]*$/,
        message: "Invalid characters entered.",
      }
    },
    lastName: {
      required: "Last name is required.",
      minLength: {
        value: userPolicy.name.minLength,
        message: `Last name should have at least ${userPolicy.name.minLength} characters.`,
      },
      maxLength: {
        value: userPolicy.name.maxLength,
        message: `Last name can't exceed ${userPolicy.name.maxLength} characters.`,
      },
      pattern: {
        value: /^[a-zA-Z ]*$/,
        message: "Invalid characters entered.",
      }
    },
    socialID: {
      required: "Social ID is required."
    },
    dateOfBirth: {
      required: "Date of birth is required."
    },
    gender: {
      required: "Gender is required."
    },
    email: {
      required: "Email is required."
    },
    mobile: {
      minLength: {
        value: userPolicy.mobile.minLength,
        message: `Mobile number should have at least ${userPolicy.mobile.minLength} numbers.`,
      },
      maxLength: {
        value: userPolicy.mobile.maxLength,
        message: `Mobile number can't exceed ${userPolicy.mobile.maxLength} numbers.`,
      },
      pattern: {
        value: /^[0-9+ ]*$/,
        message: "Invalid characters entered.",
      }
    },
    organization: {
      maxLength: {
        value: userPolicy.organization.maxLength,
        message: `Organization name can't exceed ${userPolicy.organization.maxLength} characters.`,
      }
    },
    designation: {
      maxLength: {
        value: userPolicy.designation.maxLength,
        message: `Designation can't exceed ${userPolicy.designation.maxLength} characters.`,
      }
    },
    institution: {
      maxLength: {
        value: userPolicy.institution.maxLength,
        message: `Institution name can't exceed ${userPolicy.institution.maxLength} characters.`,
      }
    },
    discipline: {
      maxLength: {
        value: userPolicy.discipline.maxLength,
        message: `Discipline can't exceed ${userPolicy.discipline.maxLength} characters.`,
      }
    }
  }
  //endregion Form Validations

  //region Form Registrations
  const {
    ref: firstNameRegisterRef,
    ...firstNameRegister
  } = register("firstName", validations.firstName);

  const {
    ref: lastNameRegisterRef,
    ...lastNameRegister
  } = register("lastName", validations.lastName);

  const {
    ref: socialIDRegisterRef,
    ...socialIDRegister
  } = register("socialID", validations.socialID);

  const {
    ref: dobRegisterRef,
    ...dobRegister
  } = register("dateOfBirth", validations.dateOfBirth);

  const {
    ref: genderRegisterRef,
    ...genderRegister
  } = register("gender", validations.gender);

  const {
    ref: emailRegisterRef,
    ...emailRegister
  } = register("email", validations.email);

  const {
    ref: mobileRegisterRef,
    onBlur: mobileRegisterOnBlur,
    ...mobileRegister
  } = register("mobile", validations.mobile);

  const {
    ref: organizationRegisterRef,
    onBlur: organizationRegisterOnBlur,
    ...organizationRegister
  } = register("organization", validations.organization);

  const {
    ref: designationRegisterRef,
    onBlur: designationRegisterOnBlur,
    ...designationRegister
  } = register("designation", validations.designation);

  const {
    ref: institutionRegisterRef,
    onBlur: institutionRegisterOnBlur,
    ...institutionRegister
  } = register("institution", validations.institution);

  const {
    ref: disciplineRegisterRef,
    onBlur: disciplineRegisterOnBlur,
    ...disciplineRegister
  } = register("discipline", validations.discipline);
  //endregion Form Registrations

  //region Methods & Event Handlers
  const {
    openDialog,
    closeDialog,
    setOnBeforeClose,
    setOnClose
  } = useDialogController();

  const {
    suppress,
    unsuppress
  } = useTippyRegistry();

  const onPhotoClick = async () => {
    suppress();
    openDialog(MediaPicker, {
      ...pages.userProfile.mediaPicker.properties,
      defaultImage: defaultImage,
      initialImage: (photo === initialPhoto ? originalPhoto : photo) || defaultImage,
      rounded: true,
      minSize: userPolicy.photo.minSize,
      maxSize: userPolicy.photo.maxSize,
      cropperProps: pages.userProfile.mediaPicker.cropperProps,
      onSave: onPhotoSelect,
      onClose: unsuppress
    });
  };

  const onPhotoSelect = (img: any) => {
    const photo = _.isBlob(img) ? URL.createObjectURL(img) : img;
    setImage(img);
    setPhoto(photo || defaultImage);

    const t = setTimeout(() => {
      mobileRef?.current?.focus();
      clearTimeout(t);
    }, 10);
  };

  const onMobileChange = async (value: any) => {
    const country = value.country;
    void setValue("country", country, {shouldDirty: true});
    const mobile = value.number;
    void setValue("mobile", mobile, {
      shouldValidate: false,
      shouldDirty: true
    });
  };

  const onMobileBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value.length < 1) return;
    const dialCode = e.target.value.replace(/^\+(\d+).*/, "$1");
    const mobile = e.target.value.replace(/^\+\d+\s*/, ``).replace(/\s/g, ``)
    if (dialCode === defaults.country?.dialCode && mobile === defaults.mobile) return;
    await mobileRegisterOnBlur(e);
    await trigger("mobile");
  };

  const onReset = () => {
    setImage(null);
    setPhoto(_user?.photo?.thumbnail || defaultProfilePic);
    setResetKey((prev) => prev + 1);
    reset(defaults);
    const t = setTimeout(() => {
      mobileRef?.current?.focus();
      clearTimeout(t);
    }, 10);
  };

  const _onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation();
    await _onSubmit();
  };

  const _onSubmit = async () => {
    if (!isValid) return;
    suppress();
    await handleSubmit(onSubmit)();
  };

  const onSubmit = async () => {
    setLoading(true);
    const values = _.omitBy(_.pick(getValues(), ['country', 'mobile', 'organization', 'designation', 'discipline']),
      (value) => _.isNil(value) || _.isNothing(value));

    const payload: IUserProfilePayload = _.omitBy({
      email: user.email,
      ...values,
      photo: image ? image : user.photo,
      device: {
        ...$store.Device.data || null,
        ...{deviceTime: new Date().toISOString()}
      }
    }, _.isNil) as IUserProfilePayload;

    await $core.saveProfile(payload).then(async (user: IUser) => {
      $store.User.user$.next(user);
      setLoading(false);
      await closeDialog(true);
      onSuccess?.();

    }).catch((e: any) => {
      setLoading(false);
      if (e?.code === HttpStatus.BAD_REQUEST && _.isArray(e?.data) && e?.data?.length > 0) {
        const fieldErrors = e?.data?.filter((f: any) => ["invalidData", "validationError"].indexOf(f?.error) < 0);
        fieldErrors.forEach((f: any) => {
          const fieldName = f?.attribute as keyof IUserProfile;
          const message = _.get(validations, `${fieldName}.${f?.error === "required" ? "required" : `${f?.error}.message`}`, null)
            .replace("{value}", getValues(fieldName));

          if (fieldName && message) {
            setError(fieldName, {
              type: "validate",
              message
            });
          }
        });
        _.toastError(`User profile contains invalid data.\nPlease correct highlighted fields and submit again.`);

      } else {
        _.toastError(_.get(e, 'message'));
      }
    });
  };

  const onScroll = () => {
    const el = contentRef.current;
    if (!el) return;

    const distanceFromTop = el.scrollTop;

    const distanceFromBottom =
      el.scrollHeight - (el.scrollTop + el.clientHeight);

    const SHOW_THRESHOLD = 5;
    const HIDE_THRESHOLD = 25;
    const TOP_HIDE_THRESHOLD = 40;

    setIsAtBottom(prev => {
      // ✅ SHOW when near bottom
      if (!prev && distanceFromBottom <= SHOW_THRESHOLD) return true;

      // ✅ HIDE when near top (priority)
      if (distanceFromTop <= TOP_HIDE_THRESHOLD) return false;

      // ✅ HIDE when moving away from bottom
      if (prev && distanceFromBottom > HIDE_THRESHOLD) return false;

      return prev;
    });
  };

  const _onBeforeClose = useCallback(() => {
    if (!isUnsaved) return true;

    return new Promise<boolean>((resolve) => {
      suppress();

      openDialog(Confirm, {
        ...pages.confirm.properties,
        text: "Discard changes?",

        onYes: async () => {
          unsuppress();
          await closeDialog(true);
          resolve(true);
        },

        onNo: async () => {
          unsuppress();
          if (lastFocusedRef?.current) {
            if (lastFocusedRef.current === "mobile") {
              mobileRef.current?.focus();
            } else {
              setFocus(lastFocusedRef.current);
            }
          }
          resolve(false);
        }
      });
    });
  }, [isUnsaved]);

  const _onClose = useCallback(() => {
    onClose?.();
  }, [onClose]);
  //endregion Event Handlers

  //region Effects
  useEffect(() => {
    if (!footerRef.current) return;

    const el = footerRef.current;

    const updateHeight = () => {
      setFooterHeight(el.scrollHeight);
    };

    updateHeight();

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;

    const el = contentRef.current;

    const checkOverflow = () => {
      requestAnimationFrame(() => {
        setIsOverflow(el.scrollHeight > el.clientHeight);
      });
    };

    checkOverflow();

    const observer = new ResizeObserver(() => checkOverflow());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setDefaultImage(defaultProfilePic);
    mobileRef?.current?.focus();
  }, []);

  useEffect(() => {
    setIsUnsaved(isDirty || (photo !== initialPhoto));
  }, [isDirty, photo]);

  useEffect(() => {
    setOnBeforeClose(_onBeforeClose);
  }, [setOnBeforeClose, _onBeforeClose]);

  useEffect(() => {
    setOnClose(_onClose);
  }, [setOnClose, _onClose]);
  //endregion Effects

  //region Toolbar Elements
  const headerElements: HeaderElement[] = [
    {
      name: "title",
      type: "span",
      position: "left",
      label: pages.userProfile.title,
      style: "text-sm sm:text-md md:text-lg font-bold text-white tracking-tight md:tracking-wider"
    },
    {
      name: "reset",
      type: "button",
      position: "right",
      icon: "fa-rotate",
      tooltip: "Reset",
      style: "text-white scale-90 transition duration-200 hover:text-generic-hover hover:scale-120",
      hidden: !isUnsaved,
      onClick: () => onReset()
    },
    {
      name: "close",
      type: "button",
      position: "right",
      icon: "fa-square-xmark",
      tooltip: "Close",
      style: "text-white scale-115 transition duration-200 hover:text-light-red hover:scale-125",
      onClick: () => closeDialog()
    }
  ];

  const footerElements: FooterElement[] = [
    {
      name: "save",
      type: "gradientButton",
      action: "submit",
      label: "Save",
      style: `btn-save btn-${isValid && isUnsaved ? 'blue transition duration-200 hover:text-generic-hover hover:scale-105' : 'gray'}`,
      disabled: !isValid,
      onClick: () => _onSubmit(),
      onEnter: () => _onSubmit()
    },
    {
      name: "cancel",
      type: "gradientButton",
      label: "Cancel",
      style: "btn-cancel btn-gray-reverse hover:text-light-red",
      onClick: () => closeDialog(),
    }
  ];
  //endregion Toolbar Elements

  //region HTML Elements
  const loadingSpinner = (
    <LoadingSpinner
      className="absolute inset-0 z-50 bg-bluegray-darker/85"
      borderRadius="rounded-sm md:rounded-md"
    />
  );

  const toolbarTop = (
    <ToolbarTop
      name={`tbt-${className}`}
      elements={headerElements}
      leftToolbarGap="gap-1"
    />
  );

  const toolbarBottom = (
    <ToolbarBottom
      ref={footerRef}
      name={`tbb-${className}`}
      elements={footerElements}
    />
  );

  const userProfileForm = (
    <form
      onSubmit={_onSubmit}
      className={`form-${className} grid px-4 gap-6 text-sm md:text-md text-custom-gray-600 dark:text-pale-white leading-relaxed`}>

      {/* Row 1 – General photo + name */}
      <div
        className="w-full h-auto flex flex-col items-center text-center">
        <div
          className="profile-picture w-fit h-fit rounded-full overflow-hidden shadow-[1px_1px_2px_1px_rgba(105,120,127,1)]! hover:shadow-[1px_1px_2px_1px_rgba(55,90,105,1)]!">
          <img
            src={photo ?? defaultImage}
            alt="General photo"
            className="w-20 h-20 object-contain rounded-full border-none cursor-pointer"
            onClick={onPhotoClick}
          />
        </div>
        <div
          className="font-semibold text-sm text-black dark:text-ghost-white tracking-wider mt-2">
          {_user.firstName || _user.lastName ? `${_user.firstName} ${_user.lastName}` : ""}
        </div>
      </div>

      {/* Row 2 – First + Last */}
      <RowGrid>
        <InputValidator
          placeholder={"First Name"}
          value={_user.firstName}
          tabIndex={-1}
          readOnly={true}
          {...firstNameRegister}
        />

        <InputValidator
          placeholder={"Last Name"}
          value={_user.lastName}
          tabIndex={-1}
          readOnly={true}
          {...lastNameRegister}
        />
      </RowGrid>

      {/* Row 3 – Login + DOB */}
      <RowGrid>
        <InputValidator
          placeholder={"Social ID"}
          value={_user.socialID}
          tabIndex={-1}
          readOnly={true}
          {...socialIDRegister}
        />

        <div
          className="flex flex-row gap-2 p-0! w-[16rem]">
          <InputValidator
            placeholder={"Date of Birth"}
            value={_user.dateOfBirth}
            tabIndex={-1}
            readOnly={true}
            {...dobRegister}
          />

          <InputValidator
            placeholder={"Gender"}
            value={_user.gender}
            tabIndex={-1}
            readOnly={true}
            {...genderRegister}
          />
        </div>
      </RowGrid>

      {/* Row 4 – Email + Mobile */}
      <RowGrid>
        <InputValidator
          placeholder={"Email"}
          value={_user.email}
          tabIndex={-1}
          readOnly={true}
          {...emailRegister}
        />

        <PhoneInput
          key={`p-${resetKey}`}
          parentRef={mobileRegisterRef}
          inputRef={mobileRef}
          placeholder="Mobile"
          minLength={userPolicy.mobile.minLength}
          maxLength={userPolicy.mobile.maxLength}
          mask={/[0-9]/}
          format="phone"
          countries={countries}
          country={country}
          initialValue={`+${country.value} ${_user?.mobile ? _user?.mobile?.replace(/(\d{3})(?=\d{3})/g, '$1 ').trim() : ''}`}
          error={errors.mobile?.message}
          name={mobileRegister.name}
          showToast={_.toastError}
          onChange={onMobileChange}
          onFocus={() => (lastFocusedRef.current = mobileRegister.name)}
          onBlur={onMobileBlur}
        />
      </RowGrid>

      {/* Row 5 – Org + Designation */}
      {!isStudent ? (
        <RowGrid>
          <InputValidator
            ref={organizationRegisterRef}
            placeholder="Organization"
            maxLength={userPolicy.organization.maxLength}
            mask={/[a-zA-Z0-9@. ]/}
            error={errors.organization?.message}
            tabIndex={isAdmin || isBizAdmin || isStaff? -1 : 0}
            readOnly={isAdmin || isBizAdmin || isStaff}
            {...organizationRegister}
            showToast={_.toastError}
            onFocus={() => (lastFocusedRef.current = organizationRegister.name)}
          />

          <InputValidator
            ref={designationRegisterRef}
            placeholder="Designation"
            maxLength={userPolicy.designation.maxLength}
            mask={/[a-zA-Z0-9@ .()-]/}
            error={errors.designation?.message}
            {...designationRegister}
            showToast={_.toastError}
            onFocus={() => (lastFocusedRef.current = designationRegister.name)}
          />
        </RowGrid>
      ) : (
        <RowGrid>
          <InputValidator
            placeholder="Institution"
            value={_user.institution}
            tabIndex={-1}
            readOnly={true}
            {...institutionRegister}
            showToast={_.toastError}
            onFocus={() => (lastFocusedRef.current = institutionRegister.name)}
          />
          <InputValidator
            ref={disciplineRegisterRef}
            placeholder="Discipline"
            maxLength={userPolicy.discipline.maxLength}
            mask={/[a-zA-Z0-9@ .()-]/}
            error={errors.discipline?.message}
            {...disciplineRegister}
            showToast={_.toastError}
            onFocus={() => (lastFocusedRef.current = disciplineRegister.name)}
          />
        </RowGrid>
      )}
    </form>
  );

  const content = (
    <div
      ref={contentRef}
      onScroll={onScroll}
      className={`
          content-${className}
          flex-1 min-h-0 box-border overflow-y-auto 
          bg-[radial-gradient(circle,#ffffff_0%,#d6d6d6_100%)] 
          dark:bg-[radial-gradient(circle,#030b11b8_0%,#030b11d8_100%)] 
          scrollbar-track-rounded-full scrollbar-thin 
          scrollbar-track-dim-white dark:scrollbar-track-transparent 
          scrollbar-thumb-custom-blue-600 
          scrollbar-hover:scrollbar-thumb-custom-blue-300 
          scrollbar-active:scrollbar-thumb-custom-blue-300
          ${isOverflow ? "pt-4" : "py-4"}
        `}
      style={{
        paddingBottom: isOverflow ? footerHeight + 16 : undefined
      }}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}>
      {userProfileForm}
    </div>
  );

  const staticFooter = (
    <div>
      {toolbarBottom}
    </div>
  );

  const fixedFooter = (
    <div
      className={`
          absolute bottom-0 left-0 right-0 z-50
          transition-transform duration-300 ease-in-out
          ${showFooter ? "translate-y-0" : "translate-y-full"}
        `}
    >
      {toolbarBottom}
    </div>
  );
  //endregion HTML Elements

  return (
    <div
      className="relative w-full h-full flex flex-col rounded-sm md:rounded-md overflow-hidden"
      onKeyDown={_onKeyDown}
      onWheel={(e) => e.stopPropagation()}>
      {loading && (
        loadingSpinner
      )}

      {toolbarTop}

      {content}

      {!isOverflow ? (
        staticFooter
      ) : (
        fixedFooter
      )}
    </div>
  );
}
